import React, { useEffect, useRef } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";
import { ema, vwap, rsi, volumeData } from "@/lib/indicators";
import { useChartPalette } from "@/lib/cosmetics";

// Default line colours passed by pages are mapped onto the active theme's equivalents.
const LINE_ROLE = { "#10B981": "support", "#EF4444": "resistance", "#06B6D4": "level", "#F8FAFC": "answer" };

/**
 * ChartEngine — thin abstraction over TradingView Lightweight Charts.
 * TRADECRAFT business logic never touches this rendering layer directly.
 * Indicators are computed only from the candles passed in (revealed data),
 * guaranteeing NO look-ahead bias.
 */
export default function ChartEngine({ candles, toggles, overlays, lines, symbol, interval, height = 380, hideHeader = false, rightOffset = 0, badge = "SIMULATED" }) {
  const mainRef = useRef(null);
  const rsiRef = useRef(null);
  const chartRef = useRef(null);
  const rsiChartRef = useRef(null);
  const seriesRef = useRef({});
  const priceLinesRef = useRef([]);
  const pal = useChartPalette();

  // init charts once
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const chart = createChart(el, {
      layout: { background: { color: "#0A0E16" }, textColor: "#94A3B8", fontFamily: "JetBrains Mono, monospace" },
      grid: { vertLines: { color: "rgba(148,163,184,0.06)" }, horzLines: { color: "rgba(148,163,184,0.06)" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: "rgba(148,163,184,0.15)" },
      timeScale: { borderColor: "rgba(148,163,184,0.15)", timeVisible: true, secondsVisible: false, rightOffset },
      localization: { locale: "en-US" },
      autoSize: true,
    });
    chartRef.current = chart;

    seriesRef.current.candle = chart.addCandlestickSeries({
      upColor: "#10B981", downColor: "#EF4444", borderUpColor: "#10B981",
      borderDownColor: "#EF4444", wickUpColor: "#34D399", wickDownColor: "#F87171",
    });
    seriesRef.current.volume = chart.addHistogramSeries({
      priceFormat: { type: "volume" }, priceScaleId: "",
    });
    seriesRef.current.volume.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    seriesRef.current.vwap = chart.addLineSeries({ color: "#06B6D4", lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
    seriesRef.current.ema9 = chart.addLineSeries({ color: "#F59E0B", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    seriesRef.current.ema20 = chart.addLineSeries({ color: "#8B5CF6", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    seriesRef.current.ema50 = chart.addLineSeries({ color: "#38BDF8", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    seriesRef.current.ema200 = chart.addLineSeries({ color: "#F472B6", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });

    // RSI mini chart
    if (rsiRef.current) {
      const rc = createChart(rsiRef.current, {
        layout: { background: { color: "#0A0E16" }, textColor: "#64748B", fontFamily: "JetBrains Mono, monospace" },
        grid: { vertLines: { color: "rgba(148,163,184,0.04)" }, horzLines: { color: "rgba(148,163,184,0.04)" } },
        rightPriceScale: { borderColor: "rgba(148,163,184,0.15)" },
        timeScale: { visible: false, borderColor: "rgba(148,163,184,0.15)" },
        localization: { locale: "en-US" },
        autoSize: true,
      });
      rsiChartRef.current = rc;
      seriesRef.current.rsi = rc.addLineSeries({ color: "#8B5CF6", lineWidth: 2, priceLineVisible: false });
      // sync time scales
      chart.timeScale().subscribeVisibleLogicalRangeChange((r) => {
        if (r) rc.timeScale().setVisibleLogicalRange(r);
      });
    }

    return () => {
      chart.remove();
      if (rsiChartRef.current) rsiChartRef.current.remove();
      chartRef.current = null;
      rsiChartRef.current = null;
      seriesRef.current = {};
    };
  }, []);

  // update data
  useEffect(() => {
    const s = seriesRef.current;
    if (!s.candle || !candles) return;
    const data = candles.map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }));
    s.candle.setData(data);
    s.volume.setData(toggles.volume ? volumeData(candles).map((v, i) => ({ ...v, color: candles[i].close >= candles[i].open ? pal.vol_up : pal.vol_down })) : []);
    s.vwap.setData(toggles.vwap ? vwap(candles) : []);
    s.ema9.setData(toggles.ema9 ? ema(candles, 9) : []);
    s.ema20.setData(toggles.ema20 ? ema(candles, 20) : []);
    s.ema50.setData(toggles.ema50 ? ema(candles, 50) : []);
    s.ema200.setData(toggles.ema200 ? ema(candles, 200) : []);
    if (s.rsi) s.rsi.setData(toggles.rsi ? rsi(candles, 14) : []);
  }, [candles, toggles, pal]);

  // chart theme (cosmetic only: colours, never data)
  useEffect(() => {
    const s = seriesRef.current;
    const c = chartRef.current;
    if (!c || !s.candle) return;
    c.applyOptions({ layout: { background: { color: pal.bg }, textColor: pal.text },
      grid: { vertLines: { color: pal.grid }, horzLines: { color: pal.grid } },
      rightPriceScale: { borderColor: pal.border }, timeScale: { borderColor: pal.border } });
    s.candle.applyOptions({ upColor: pal.up, downColor: pal.down, borderUpColor: pal.up, borderDownColor: pal.down,
      wickUpColor: pal.wick_up, wickDownColor: pal.wick_down });
    ["vwap", "ema9", "ema20", "ema50", "ema200"].forEach((k) => s[k].applyOptions({ color: pal[k] }));
    if (rsiChartRef.current && s.rsi) {
      rsiChartRef.current.applyOptions({ layout: { background: { color: pal.bg }, textColor: pal.text_muted },
        grid: { vertLines: { color: pal.grid }, horzLines: { color: pal.grid } },
        rightPriceScale: { borderColor: pal.border } });
      s.rsi.applyOptions({ color: pal.rsi });
    }
  }, [pal]);

  // overlays: entry / stop / target price lines
  useEffect(() => {
    const s = seriesRef.current;
    if (!s.candle) return;
    priceLinesRef.current.forEach((pl) => { try { s.candle.removePriceLine(pl); } catch (e) {} });
    priceLinesRef.current = [];
    const add = (price, color, title) => {
      if (price == null) return;
      priceLinesRef.current.push(
        s.candle.createPriceLine({ price, color, lineWidth: 2, lineStyle: 2, axisLabelVisible: true, title })
      );
    };
    if (overlays) {
      add(overlays.entry, pal.entry, "ENTRY");
      add(overlays.stop, pal.stop, "STOP");
      add(overlays.target, pal.target, "TARGET");
    }
    if (lines) lines.forEach((l) => add(l.price, pal[LINE_ROLE[(l.color || "#06B6D4").toUpperCase()]] || l.color, l.title || ""));
  }, [overlays, lines, candles, pal]);

  return (
    <div data-testid="chart-engine" data-chart-bg={pal.bg} className="w-full">
      {!hideHeader && (
      <div className="flex items-center justify-between px-1 pb-2">
        <div className="flex items-center gap-2">
          <span className="font-heading font-bold text-lg tracking-tight" data-testid="chart-symbol">{symbol}</span>
          <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-tc-sub">{interval}</span>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-tc-pass/15 text-tc-pass tracking-wide" data-testid="chart-badge">{badge}</span>
        </div>
        <span className="text-[10px] font-mono text-tc-muted">Powered by TradingView Lightweight Charts™</span>
      </div>
      )}
      <div ref={mainRef} data-testid="chart-main" style={{ height }} className="w-full rounded-lg overflow-hidden border border-white/10" />
      {toggles.rsi && (
        <div className="mt-2">
          <div className="text-[10px] font-mono text-tc-mastery px-1 mb-1">RSI (14)</div>
          <div ref={rsiRef} data-testid="chart-rsi" style={{ height: 90 }} className="w-full rounded-lg overflow-hidden border border-white/10" />
        </div>
      )}
    </div>
  );
}
